import { useRef } from "react";
import { Node } from "@tiptap/core";
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { ImageUp, Loader2, Trash2 } from "lucide-react";
import { useUpload } from "@/components/admin/Fields";
import { imgUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const SIZES = [{ v: "normal", l: "Normal" }, { v: "wide", l: "Brei" }, { v: "full", l: "Full breidd" }];

const FigureView = ({ node, updateAttributes, deleteNode, selected }) => {
  const fileRef = useRef(null);
  const { upload, uploading } = useUpload();
  const swap = async (file) => { const url = await upload(file); if (url) updateAttributes({ src: url }); };
  return (
    <NodeViewWrapper as="figure" data-size={node.attrs.size} className={cn("hk-figure group/fig", selected && "is-selected")} data-testid="editor-figure">
      <div className="hk-block-tools" contentEditable={false} data-testid="editor-figure-tools">
        <span className="hk-seg">{SIZES.map((s) => <button key={s.v} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => updateAttributes({ size: s.v })} className={cn(node.attrs.size === s.v && "is-on")} data-testid={`editor-figure-size-${s.v}`}>{s.l}</button>)}</span>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => fileRef.current?.click()} disabled={uploading} title="Byt bilete" aria-label="Byt bilete">{uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageUp className="h-3.5 w-3.5" />}</button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={deleteNode} title="Fjern biletet" aria-label="Fjern biletet" className="is-danger" data-testid="editor-figure-remove"><Trash2 className="h-3.5 w-3.5" /></button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { swap(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      <img src={imgUrl(node.attrs.src, 1600)} alt={node.attrs.alt || ""} draggable={false} data-drag-handle />
      <NodeViewContent as="figcaption" className="hk-figcaption" />
    </NodeViewWrapper>
  );
};

/** <figure data-size><img><figcaption>caption</figcaption></figure> – the caption is editable inline content. */
export const Figure = Node.create({
  name: "figure",
  group: "block",
  content: "inline*",
  draggable: true,
  isolating: true,
  addAttributes() {
    return {
      src: { default: null, parseHTML: (el) => el.querySelector("img")?.getAttribute("src") || null },
      alt: { default: "", parseHTML: (el) => el.querySelector("img")?.getAttribute("alt") || "" },
      size: { default: "normal", parseHTML: (el) => el.getAttribute("data-size") || "normal" },
    };
  },
  parseHTML() { return [{ tag: "figure", contentElement: "figcaption", getAttrs: (el) => (el.querySelector("img") ? null : false) }]; },
  renderHTML({ node }) { return ["figure", { "data-size": node.attrs.size }, ["img", { src: node.attrs.src, alt: node.attrs.alt || "" }], ["figcaption", {}, 0]]; },
  addNodeView() { return ReactNodeViewRenderer(FigureView); },
  addCommands() {
    return {
      insertFigureAt: (pos, attrs) => ({ chain }) => chain().insertContentAt(pos, { type: this.name, attrs: { size: "normal", ...attrs } }).run(),
    };
  },
});
